import { useEffect, useMemo, useRef, useState } from "react";
import { Card, CardBody, Col, Input, Row, Table } from "reactstrap";
import isEqual from "lodash.isequal";
import { DOCUMENT_LIMIT_CATALOG, DocumentLimitNode } from "@/security/documentLimits.catalog";

export type DocumentLimitValues = Record<string, number>;

type Props = {
    nodes?: DocumentLimitNode[];
    /** Valores iniciales (documentTypeId -> tope mensual). */
    initialValues?: DocumentLimitValues;
    onChange?: (values: DocumentLimitValues) => void;
    readOnly?: boolean;
};

/**
 * Editor de topes mensuales por tipo de documento. Un input numérico por tipo;
 * vacío o 0 = ilimitado. Emite solo los valores positivos (documentTypeId -> número).
 */
export default function DocumentLimitsEditor({ nodes = DOCUMENT_LIMIT_CATALOG, initialValues, onChange, readOnly }: Props) {
    const computedInitial: DocumentLimitValues = useMemo(() => {
        const base: DocumentLimitValues = {};
        for (const n of nodes) {
            const value = Number(initialValues?.[n.id] ?? 0);
            if (Number.isFinite(value) && value > 0) base[n.id] = Math.floor(value);
        }
        return base;
    }, [nodes, initialValues]);

    const [values, setValues] = useState<DocumentLimitValues>(computedInitial);

    useEffect(() => {
        if (!isEqual(values, computedInitial)) {
            setValues(computedInitial);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [computedInitial]);

    const lastEmittedRef = useRef<DocumentLimitValues>(values);
    const onChangeRef = useRef(onChange);
    useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

    useEffect(() => {
        if (!isEqual(values, lastEmittedRef.current)) {
            lastEmittedRef.current = values;
            onChangeRef.current?.(values);
        }
    }, [values]);

    const setValue = (id: string, raw: string) => {
        const num = Number(raw);
        setValues(prev => {
            const next = { ...prev };
            if (!raw || !Number.isFinite(num) || num <= 0) {
                delete next[id]; // vacío/0 = ilimitado
            } else {
                next[id] = Math.floor(num);
            }
            return isEqual(next, prev) ? prev : next;
        });
    };

    return (
        <Row className="g-3">
            <Col md="12">
                <Card>
                    <CardBody className="p-0">
                        <Table responsive hover className="mb-0 align-middle">
                            <thead>
                                <tr>
                                    <th>Tipo de documento</th>
                                    <th style={{ width: 200 }}>Máximo por mes</th>
                                </tr>
                            </thead>
                            <tbody>
                                {nodes.map(n => (
                                    <tr key={n.id}>
                                        <td>{n.label}</td>
                                        <td>
                                            <Input
                                                type="number"
                                                min={0}
                                                step={1}
                                                placeholder="Ilimitado"
                                                value={values[n.id] ?? ""}
                                                onChange={e => setValue(n.id, e.target.value)}
                                                disabled={readOnly}
                                                aria-label={`Máximo mensual de ${n.label}`}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </CardBody>
                </Card>
            </Col>
        </Row>
    );
}
